import React, { useState, useEffect } from 'react';
import { apiRequest } from '../utils/api';

const LeadsList = ({ onLeadSelect, onCreateNew }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  useEffect(() => {
    fetchLeads();
  }, [pagination.page, searchTerm, statusFilter]);

  const fetchLeads = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
      });
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);

      const data = await apiRequest(`/admin/leads?${params.toString()}`);
      if (data && data.success) {
        setLeads(data.data || []);
        setPagination(prev => ({
          ...prev,
          total: data.pagination?.total || 0,
          pages: data.pagination?.pages || 0,
        }));
      } else {
        setError(data.message || 'حدث خطأ في جلب البيانات');
      }
    } catch (error) {
      setError('حدث خطأ في الاتصال بالخادم');
      console.error('Error fetching leads:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      lead: { label: 'طلب جديد', class: 'badge-info' },
      setup: { label: 'قيد الإعداد', class: 'badge-warning' },
      converted: { label: 'تم التحويل', class: 'badge-success' },
      rejected: { label: 'مرفوض', class: 'badge-danger' },
    };
    const statusInfo = statusMap[status] || { label: status, class: 'badge-secondary' };
    return <span className={`badge ${statusInfo.class}`}>{statusInfo.label}</span>;
  };

  const getSourceBadge = (source) => {
    return source === 'lead-generation' ? 'طلب أولي' : 'إعداد كامل';
  };

  if (loading && leads.length === 0) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        جاري التحميل...
      </div>
    );
  }

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">قائمة الطلبات</h2>
        </div>

        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          <input
            type="text"
            placeholder="بحث..."
            value={searchTerm}
            onChange={handleSearch}
            className="form-control"
            style={{ flex: 1, minWidth: '200px' }}
          />
          <select
            value={statusFilter}
            onChange={handleStatusFilter}
            className="form-control"
            style={{ minWidth: '150px' }}
          >
            <option value="">جميع الحالات</option>
            <option value="lead">طلب جديد</option>
            <option value="setup">قيد الإعداد</option>
            <option value="converted">تم التحويل</option>
            <option value="rejected">مرفوض</option>
          </select>
        </div>

        {error && (
          <div className="alert alert-danger">
            <span>❌</span>
            <span>{error}</span>
          </div>
        )}

        {leads.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📝</div>
            <div className="empty-state-title">لا توجد طلبات</div>
            <div className="empty-state-text">لم يتم العثور على أي طلبات</div>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>الاسم</th>
                    <th>اسم المتجر</th>
                    <th>البريد الإلكتروني</th>
                    <th>الهاتف</th>
                    <th>الدومين</th>
                    <th>المصدر</th>
                    <th>الحالة</th>
                    <th>تاريخ الإنشاء</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead._id}>
                      <td><strong>{lead.storeOwnerName}</strong></td>
                      <td>{lead.storeName}</td>
                      <td>{lead.email}</td>
                      <td>{lead.phoneNumber}</td>
                      <td>{lead.domain || '—'}</td>
                      <td>{getSourceBadge(lead.source)}</td>
                      <td>{getStatusBadge(lead.status)}</td>
                      <td style={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                        {new Date(lead.createdAt).toLocaleDateString('ar-SA')}
                      </td>
                      <td>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => onLeadSelect(lead)}
                        >
                          التفاصيل
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.pages > 1 && (
              <div style={{ padding: '1.5rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  السابق
                </button>
                <span style={{ padding: '0.5rem 1rem', color: 'var(--gray-600)', fontSize: '0.875rem' }}>
                  صفحة {pagination.page} من {pagination.pages}
                </span>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages}
                >
                  التالي
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default LeadsList;
